import React, { useCallback, useMemo } from 'react';
import { Table } from 'antd';
import ItemsListSkeleton from './ItemsListSkeleton';
import { DndContext, MouseSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useNavigate } from 'react-router-dom';

import useDateFormat from '../../hooks/useDateFormat';
import useTimeFormat from '../../hooks/useTimeFormat';
import useEnsureCards from '../../hooks/useEnsureCards';
import useWindowClass from '../../hooks/useWindowClass';
import useUnitSystem from '../../hooks/useUnitSystem';
import MeasurementValue from '../common/MeasurementConversions';
import ValueWithAction, { hasValueAction } from '../common/ValueAction';
import MaskedValue from '../common/MaskedValue';
import { isMaskedElement } from '../../utils/maskedElements';
import LookupRefs from '../common/LookupRefs';
import RecordBadge, { RecordTitle } from '../common/RecordBadge';
import IntakePendingTag from './IntakePendingTag';
import DemoTag from './DemoTag';
import CopiedFromNote from './CopiedFromNote';
import { isPendingIntake, isDemoItem } from '../../services/records';
import { formatDisplayDateTime, formatSourceDate } from '../../utils/dateDisplay';
import { createPrimaryDateResolver, describePrimaryDate, resolveItemPrimaryDate } from '../../utils/cards';
import { normalizeId } from '../../utils/ids';
import getItemDisplayValue from '../../utils/items';
import { createLookupResolver } from '../../utils/lookups';
import { canManage, canWriteCard } from '../../utils/permissions';
import ItemActionsMenu from './ItemActionsMenu';
import ItemOpenButton from './ItemOpenButton';
import {
	formatPrimaryDate,
	formatValue,
	getChoiceMeta,
} from './itemValueDisplay';
import UserValue from '../users/UserValue';
import UserRef from '../users/UserRef';
import AttestationModal from '../users/AttestationModal';
import ItemConflictModal from './ItemConflictModal';
import { isEmptyUserValue as isEmptyUser } from '../../utils/userElements';
import { markdownToPlainText } from '../../utils/markdown';
import { useScreenShortcuts } from '../../contexts/ShortcutsContext';
import TableCellEditor from './TableCellEditor';
import TableCell, { CellMenuContext } from './TableCell';
import useCellValueMenu from './useCellValueMenu';
import useCellEdit from './useCellEdit';
import {
	byNameOf,
	cellEditability,
	columnKey,
	columnLabel,
	defaultTableColumns,
	resolveTableColumns,
} from './cardTableModel';
import { LINKED_ROW_EXCLUDED_ACTIONS, isLinkedRow, itemHomeCollectionId, rowSourceOf } from '../../utils/linkedRecords';
import { familyMemberOf, hasSubCollections } from '../../utils/collectionFamily';
import { rowOwnCollection, rowSourceIdentity } from '../../utils/recordQualifier';
import ItemTagChips from '../tags/ItemTagChips';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalTags } from '../../contexts/TagsContext';
import { rememberedItemTagTitles } from '../../hooks/useTagSearch';
import { itemTagEntries } from '../../utils/itemTags';
import ChoiceChips from './ChoiceChips';
import { isChoiceList } from '../../utils/choiceValues';
import { FileCellValue } from '../common/FileValue';
import { filesOf, isFileElement } from '../../utils/fileValues';
import { readCount } from '../../utils/confirmationValues';
import { groupCellSummary } from '../../utils/repeatingGroupEntries';
import useItemsTableRowProps, { itemRowKey, memoiseCellRenders } from './itemsTableRowProps';
import useStableCallback from '../../hooks/useStableCallback';
import useTableHeaders from './useTableHeaders';
import { RANK_HANDLE_WIDTH, RankHandle, RankSortableRow } from './ItemsTableRank';
import { DESKTOP_TITLE_WIDTH, phoneTitleWidth, useStickyHeaderOffset, useViewportWidth } from './useTableViewport';

// A long-text column reads one clamped line at this width; everything else
// sizes to its content (the table scrolls sideways inside its container).
const LONG_TEXT_WIDTH = 280;
// No pool given: one empty pool, so the columns keep their identity (KPR-1557).
const NO_POOL = Object.freeze({});

// Open + ⋯ (two small icon buttons) plus the cell padding.
const ACTIONS_WIDTH = 84;

/**
 * The items list's table view. Same feed, same selection model as the
 * grid: row click selects (opens the detail); the trailing Open · ⋯ carry
 * the item's door and its full action set. Element columns appear only
 * when every visible item shares a card type — a mixed list gets Title /
 * Type / Updated. With `expandedItemId` + `renderExpanded` the selected
 * row opens the detail beneath it (the phone layout).
 *
 * The table scrolls horizontally inside its own container, never the
 * page: the title column is frozen at the left and the actions column at
 * the right (`fixed`), and the header sticks beneath the context bar
 * (`sticky`) so the reference survives scrolling in both directions.
 *
 * A row is the item's OWN surface: its title is the chip + plain
 * 13.5px/600 text (no pill), the type a plain word (the chip already
 * carries the identity), Updated right-aligned in tabular figures. The
 * selection lives on the row — action tint + a 2px left edge — never on
 * the title.
 */
export default function ItemsTable({
	items,
	cardsById,
	// cardPrimaryDate(id) from the card cache; optional.
	resolvePrimaryDate: resolvePrimaryDateOverride = null,
	itemsById = NO_POOL,
	getElementsForCard,
	loading = false,
	selectedItemId = null,
	onSelectItem,
	collection = null,
	collectionId,
	expandedItemId = null,
	renderExpanded,
	onItemDeleted: onItemDeletedProp,
	// ---- multi-selection (owned by the page; see hooks/useItemSelection) ----
	// A Set of item ids, or null when the surface has no selection at all
	// (the public shared view). Keyed by ID and never by row index: the feed
	// re-sorts and re-pages under it, and `preserveSelectedRowKeys` keeps the
	// ids of rows that have paged out of view.
	selectedIds = null,
	// (ids) — the whole key set after antd's own change (a click, a shift
	// range, the header box). The page owns the set; this reports it.
	onSelectionChange,
	// (id) — the row the pointer was last on, i.e. the next range anchor.
	onSelectionAnchor,
	// (id, { selected, shiftKey }) — a shift-click on the ROW BODY, which
	// antd's checkbox column never sees.
	onToggleSelect,
	// (item) -> a sentence, when this row cannot be acted on at all.
	selectionDisabledFor,
	// ---- the card-specific table (docs/platform-standards.md §5) ----
	// cardId -> the `table` layout that applies (hooks/useTableLayouts):
	// which columns a single-card list shows. Absent = every field.
	tableLayouts = null,
	// (item) — a cell edit landed (or a stale row was reloaded): the page
	// swaps the settled document into its feed. Its presence is what turns
	// inline editing on; the public shared view passes none.
	onItemUpdated: onItemUpdatedProp = null,
	// () — reload the feed, when a refused row could not be re-read.
	onReload: onReloadProp = null,
	// A parent collection's family list: a Sub-collection column after the
	// title — each row's collection as a badge. Off by default (the
	// "in [Health]" beside the title already says it).
	showCollectionColumn = false,
	// A list that belongs to NO collection — the tag page (tags T5), whose
	// rows come from every collection the reader reaches: each title is
	// qualified by the row's own collection, and a row's ⋯ offers what reading
	// allows (the list holds no collection to say who may write).
	crossCollection = false,
	// A list sorted by a manual order (KPR-1396, hooks/useRankMove): rows
	// reorder by drag, and each row's ⋯ offers Move up / down / to top.
	rankOrder = null,
	// The header controls (KPR-1638): `{ sort: levelsProps, onColumnsChanged }`
	// from the items page (components/items/useTableHeaders). Absent = plain headers.
	headerMenu = null,
}) {
	// The columns call these; stable however the page builds them (KPR-1557).
	const onItemDeleted = useStableCallback(onItemDeletedProp);
	const onItemUpdated = useStableCallback(onItemUpdatedProp);
	const onReload = useStableCallback(onReloadProp);
	// The signed-in user's measurement preference (the table is never public).
	const unitSystem = useUnitSystem();
	// compact (§18): a narrower frozen title column with a larger chip.
	const { compact: isMobile } = useWindowClass();
	const viewportWidth = useViewportWidth();
	const titleWidth = isMobile ? phoneTitleWidth(viewportWidth) : DESKTOP_TITLE_WIDTH;
	const stickyOffset = useStickyHeaderOffset();

	const resolveElements = useMemo(() => {
		if (typeof getElementsForCard !== 'function') return () => [];
		return cardId => getElementsForCard(cardId) || [];
	}, [getElementsForCard]);

	const resolveLookupValue = useMemo(() => createLookupResolver({
		itemsById,
		cardsById,
		getElementsForCard: resolveElements,
	}), [itemsById, cardsById, resolveElements]);

	const uniqueCardId = useMemo(() => {
		const ids = new Set((items || []).map(item => normalizeId(item?.card_id)).filter(Boolean));
		return ids.size === 1 ? Array.from(ids)[0] : null;
	}, [items]);

	// The date columns redraw when either preference changes (memoiseCellRenders).
	const formats = `${useDateFormat()}|${useTimeFormat()}`;
	useEnsureCards(items); // a card added since the page loaded is fetched, not blank
	// Each card's primary date (utils/cards.js): the column every row has.
	// When the rows share a card the header names the date ("Due",
	// "Created"); a mixed list says "Primary date" and each cell's title
	// says which — as does a shared ORDERED spec, whose header keeps
	// "Primary date" with the list in its tooltip ("Scheduled, then Due,
	// then Created") and whose cells each say which entry they show.
	const resolvePrimaryDate = useMemo(
		() => resolvePrimaryDateOverride || createPrimaryDateResolver(cardsById || {}),
		[resolvePrimaryDateOverride, cardsById]
	);
	const uniquePrimaryDate = uniqueCardId ? resolvePrimaryDate(uniqueCardId) : null;
	const uniqueOrdered = uniquePrimaryDate?.kind === 'ordered';
	// The element the column stands in for: the chosen date, or an ordered
	// list's LEAD — its fallbacks keep their own columns.
	const uniquePrimaryElement = uniqueOrdered ? uniquePrimaryDate.entries[0]?.element?.name : uniquePrimaryDate?.element?.name;

	// ---- The single-card table: its columns, and editing in place ----
	const navigate = useNavigate();
	const cardDefs = useMemo(
		() => (uniqueCardId ? resolveElements(uniqueCardId) : []),
		[uniqueCardId, resolveElements]
	);
	// Last declaration wins, as getElementsForCard merges (cardTableModel byNameOf).
	const defsByName = useMemo(() => byNameOf(cardDefs), [cardDefs]);
	// A file element's column shows only while the reader passes the
	// attachments gate (docs/plans/data-types-file-element.md §5): the
	// collection's switch, or — with no collection to ask — whether the rows
	// came with `files` facts. A boolean, so a page of rows appended or one
	// row edited leaves the columns (and every memoised cell) as they were.
	const filesShown = collection ? collection.allowAttachments === true : (items || []).some(row => row?.files);
	// The layout's columns, or every field in form order (cardTableModel).
	const resolvedColumns = useMemo(() => {
		if (!uniqueCardId) return null;
		const defaults = defaultTableColumns(cardDefs, {
			primaryElement: uniquePrimaryElement,
			primaryIsUpdated: uniquePrimaryDate?.kind === 'updated',
		});
		return resolveTableColumns(tableLayouts?.[uniqueCardId]?.body, cardDefs, defaults);
	}, [uniqueCardId, cardDefs, uniquePrimaryElement, uniquePrimaryDate, tableLayouts]);
	const tableColumns = useMemo(() => (resolvedColumns && !filesShown
		? resolvedColumns.filter(column => !column.element || !isFileElement(defsByName.get(column.element)))
		: resolvedColumns), [resolvedColumns, filesShown, defsByName]);

	const inlineEdit = typeof onItemUpdated === 'function' && Boolean(uniqueCardId);
	const cellEdit = useCellEdit({ elementDefs: cardDefs, onItemUpdated, onReload });
	const { editing, pending, open: openCell, commit: commitCell, cancel: cancelCell } = cellEdit;
	// Rows draw as they WILL stand while a save is in flight (optimistic);
	// a refusal drops the overlay and the stored row shows again.
	const rows = useMemo(
		() => (inlineEdit && Object.keys(pending).length
			? (items || []).map(item => pending[normalizeId(item?._id)] || item)
			: items),
		[items, pending, inlineEdit]
	);
	const tableId = useMemo(() => `card-table-${uniqueCardId || 'mixed'}`, [uniqueCardId]);
	// The rows on screen, for a manual order's moves (KPR-1396).
	const rowsRef = React.useRef(rows);
	rowsRef.current = rows;
	// A linked row opens in the collection it lives in (utils/linkedRecords).
	const openItem = useCallback(
		item => navigate(`/collections/${itemHomeCollectionId(item, collectionId)}/items/${normalizeId(item?._id)}`),
		[navigate, collectionId]
	);
	useScreenShortcuts('Item table', inlineEdit ? [
		{ keys: ['enter'], label: 'Edit the focused cell' },
		{ keys: ['left', 'right'], label: 'Move between cells' },
		{ keys: ['esc'], label: 'Cancel the cell you are editing' },
	] : []);

	// The tags column's names: the collection's vocabulary (its own tags and
	// its parent's) and my private tags, asked for once for the whole table
	// and only when a column shows tags; each row's own `tagTitles` answer
	// for the rest (a sub-collection's tags, item tags, a tombstone).
	const showsTags = Boolean(uniqueCardId) && tableColumns.some(column => column.system === 'tags');
	const { tags: tagVocabulary } = useCollectionTags(collectionId, { enabled: showsTags });
	const privateTags = useOptionalTags()?.tags || null;
	// The value menu (right-click, Menu key, long press, the hover ⋯): one for the table.
	const cellMenu = useCellValueMenu({ headerMenu, defsByName, resolveLookupValue, unitSystem, tagVocabulary, privateTags });

	const baseColumns = useMemo(() => {
		const titleColumn = (extra = {}) => ({
			title: 'Title',
			key: 'title',
			fixed: 'left',
			width: titleWidth,
			className: 'items-table__title-cell',
			render: (_, item) => {
				const card = cardsById?.[normalizeId(item?.card_id)] || null;
				const elementDefs = resolveElements(item?.card_id);
				// A record a collection link brought in — or one living in a
				// sub-collection of this one, or a parent's read by reference —
				// wears its collection in its own title: the qualified pill's
				// plain form, the relation said in the segment (a linked row's
				// lock) and in its tooltip (unless the Sub-collection column
				// already says where).
				const source = rowSourceOf(item, collection);
				const showSource = Boolean(source) && !(source.via === 'family' && showCollectionColumn);
				const titleNode = (
					<RecordTitle
						kind="item"
						record={card}
						size={isMobile ? 'md' : 'sm'}
						className="items-table__title"
						title={getItemDisplayValue({ card, elementDefs, item, resolveLookupValue })}
						collection={showSource ? rowSourceIdentity(source) : (crossCollection ? rowOwnCollection(item) : null)}
						collectionRelation={showSource ? source.via : null}
					/>
				);
				// In a combined list a copy names its original's collection (KPR-1024).
				if (crossCollection && item?.copiedFrom) {
					return (
						<span className="items-table__title-with-state">
							{titleNode}
							<CopiedFromNote item={item} compact />
						</span>
					);
				}
				// A submission waiting for review says so on its row: the
				// queue is the list filtered to them, and "any" mixes both.
				if (!isPendingIntake(item) && !isDemoItem(item)) return titleNode;
				return (
					<span className="items-table__title-with-state">
						{titleNode}
						<IntakePendingTag item={item} />
						<DemoTag item={item} />
					</span>
				);
			},
			...extra,
		});

		const renderElementValue = (def, item, column = null) => {
			const value = item?.elements?.[def.name];
			// A masked element (KPR-1348): its mask, with Show and Copy.
			if (isMaskedElement(def)) return <MaskedValue element={def} value={value} prefs={{ unitSystem }} resetKey={item?._id} />;
			// A repeating group's cell is its entry count, or — when the layout
			// chose a column — that column of its first entries (KPR-683).
			if (def.dataType === 'repeating-group') {
				const summary = groupCellSummary(def, value, column?.display, (columnDef, raw) => formatValue(columnDef, raw, { unitSystem, resolveLookupValue }));
				if (summary.text === null) return summary.count ? formatValue(def, value) : '—';
				if (!summary.text) return '—';
				// A MASKED column chosen for the cell (KPR-1353): one mask for the
				// cell, with Show (the first entries' values, joined as the cell
				// would print them) and Copy — never the values themselves.
				if (isMaskedElement(summary.column)) {
					const revealed = groupCellSummary(def, value, column?.display, (columnDef, raw) => formatValue(columnDef, raw, { unitSystem, resolveLookupValue, reveal: true })).text;
					return (
						<span className="card-table__masked-entries">
							<MaskedValue element={summary.column} value={revealed} text={revealed} resetKey={item?._id} />
							{summary.more > 0 ? <span className="card-table__more">{` +${summary.more}`}</span> : null}
						</span>
					);
				}
				return (
					<span className="card-table__clamp">
						{summary.text}
						{summary.more > 0 ? <span className="card-table__more">{` +${summary.more}`}</span> : null}
					</span>
				);
			}
			// A 32 px `sm`, or the chip at xs; a list its first and "+N"; an id
			// the read has no facts for, nothing (components/common/FileValue).
			if (def.dataType === 'file') {
				return filesOf(item, value).length ? <FileCellValue item={item} value={value} /> : '—';
			}
			if (def.dataType === 'user') {
				return isEmptyUser(value) ? '—' : <UserValue element={def} value={value} collectionId={collectionId} size="xs" />;
			}
			if (def.dataType === 'card-lookup') {
				// Its title, the door to filtering by it; a reference into
				// another collection is its qualified badge (LookupRefs).
				return <LookupRefs element={def} value={value} resolveLookupValue={resolveLookupValue} size="xs" />;
			}
			if (def.dataType === 'measurement') {
				return <MeasurementValue element={def} value={value} unitSystem={unitSystem} />;
			}
			// A "Me too" count (KPR-390) is a number in a column of numbers:
			// 0 included, since absent reads as 0 — a column that skipped the
			// zeros would read like one that failed to load.
			if (def.dataType === 'confirmations') return formatValue(def, readCount(value));
			// A long text reads as one clamped line; rich text as its words,
			// never its markup (utils/markdown is the one place it is read).
			if (def.dataType === 'rich-text') {
				const text = value ? markdownToPlainText(String(value)) : '';
				return text ? <span className="card-table__clamp">{text}</span> : '—';
			}
			if (def.dataType === 'text-large') {
				return value ? <span className="card-table__clamp">{String(value)}</span> : '—';
			}
			// A phone, email, location or URL cell carries its
			// own action (platform standards §4).
			if (hasValueAction(def.dataType)) {
				return <ValueWithAction element={def} value={value} />;
			}
			// A Choice that allows multiple values: its chips, wrapping in the
			// cell (data types PR 5).
			if (isChoiceList(def, value)) return <ChoiceChips element={def} value={value} />;
			const choiceMeta = getChoiceMeta(def, value);
			return choiceMeta.label || formatValue(def, value, { unitSystem }) || '—';
		};

		const primaryDateColumn = (extra = {}) => ({
			title: uniquePrimaryDate && !uniqueOrdered
				? uniquePrimaryDate.label
				: <span title={uniqueOrdered ? describePrimaryDate(uniquePrimaryDate) : undefined}>Primary date</span>,
			key: 'primaryDate',
			width: 170,
			align: 'right',
			className: 'items-table__date',
			render: (_, item) => {
				const spec = resolvePrimaryDate(item?.card_id);
				const resolved = resolveItemPrimaryDate(spec, item);
				const text = formatPrimaryDate(resolved, resolved.value);
				return <span title={uniquePrimaryDate && !uniqueOrdered ? undefined : resolved.label}>{text || '—'}</span>;
			},
			...extra,
		});

		const timestampColumn = (field, title, extra = {}) => ({
			title,
			key: field === 'updatedAt' ? 'updated' : 'created',
			width: 170,
			align: 'right',
			className: 'items-table__updated',
			render: (_, item) => {
				const raw = field === 'updatedAt' ? (item?.updatedAt || item?.updated_at) : (item?.createdAt || item?.created_at);
				return raw ? formatDisplayDateTime(raw) : '—';
			},
			...extra,
		});

		const actionsColumn = {
			title: '',
			key: 'actions',
			fixed: 'right',
			width: ACTIONS_WIDTH,
			align: 'right',
			className: 'items-table__actions-cell',
			render: (_, item) => {
				const cardId = normalizeId(item?.card_id);
				const card = cardsById?.[cardId] || null;
				// A linked row opens in its own collection and changes nothing here.
				const linked = isLinkedRow(item);
				const homeId = itemHomeCollectionId(item, collectionId);
				return (
					<span
						className="items-table__actions"
						onClick={event => event.stopPropagation()}
						role="presentation"
					>
						<ItemOpenButton item={item} collectionId={homeId} />
						<ItemActionsMenu
							surface="card"
							item={item}
							card={card}
							elementDefs={resolveElements(item?.card_id)}
							resolveLookupValue={resolveLookupValue}
							collectionId={homeId}
							collection={linked ? null : collection}
							exclude={linked ? LINKED_ROW_EXCLUDED_ACTIONS : undefined}
							canEdit={linked || crossCollection ? false : (collection ? canWriteCard(collection, cardId) : true)}
							// Unlock… is a manager's, and the record policy reads
							// through the card's parents (KPR-155).
							canManage={linked || crossCollection ? false : Boolean(collection && canManage(collection))}
							cardsById={cardsById}
							onDeleted={onItemDeleted}
							onItemUpdated={typeof onItemUpdated === 'function' ? onItemUpdated : undefined}
							extraActions={rankOrder?.active ? rankOrder.menuActions(item, rowsRef.current) : undefined}
							buttonProps={{ type: 'text', size: 'small' }}
						/>
					</span>
				);
			},
		};

		// A mixed list: Title · Type · Primary date · Updated, as it always was.
		if (!uniqueCardId) {
			return [
				titleColumn(),
				{
					title: 'Type',
					key: 'cardType',
					width: 180,
					className: 'items-table__type',
					render: (_, item) => {
						const card = cardsById?.[normalizeId(item?.card_id)] || null;
						return card ? (card?.label?.singular || card?.name || 'Card') : '—';
					},
				},
				primaryDateColumn(),
				timestampColumn('updatedAt', 'Updated'),
				actionsColumn,
			];
		}

		// ONE card: its table — the layout's columns, or every field. A
		// frozen column past the title is honoured on desktop only: on a
		// phone the title alone already takes ~44% of the width, and a second
		// pinned column would leave nothing to scroll.
		const cardMeta = cardsById?.[uniqueCardId] || null;
		return [
			...tableColumns.map((column, colIndex) => {
				const fixed = column.frozen && (column.system === 'title' || !isMobile) ? { fixed: 'left' } : {};
				const width = column.width ? { width: column.width } : {};
				const key = columnKey(column);
				// Every value cell is a TableCell once inline editing is on, so
				// the arrows walk the whole row — read-only ones included.
				const wrap = (item, rowIndex, content, editability) => (inlineEdit ? (
					<TableCell
						tableId={tableId}
						rowIndex={rowIndex}
						colIndex={colIndex}
						editability={editability}
						pending={Boolean(pending[normalizeId(item?._id)])}
						editing={editing?.itemId === normalizeId(item?._id) && editing?.name === column.element}
						item={item}
						columnKey={key}
						onOpen={() => openCell(item, column.element)}
						onOpenItem={() => openItem(item)}
					>
						{content}
					</TableCell>
				) : content);

				if (column.system === 'title') {
					const base = titleColumn(width);
					return { ...base, render: (value, item, rowIndex) => wrap(item, rowIndex, base.render(value, item), null) };
				}
				if (column.system) {
					const readOnly = item => cellEditability({ column, item });
					let base;
					if (column.system === 'primaryDate') base = primaryDateColumn();
					else if (column.system === 'updatedAt') base = timestampColumn('updatedAt', 'Updated');
					else if (column.system === 'createdAt') base = timestampColumn('createdAt', 'Created');
					else if (column.system === 'sourceCreatedAt') {
						base = {
							title: 'Created in source',
							key: 'sourceCreatedAt',
							width: 170,
							align: 'right',
							className: 'items-table__updated',
							render: (_, item) => (item?.source?.createdAt ? formatSourceDate(item.source.createdAt) : '—'),
						};
					} else if (column.system === 'tags') {
						// The item's tags as badges, READ-ONLY in a cell
						// (docs/plans/tags.md §5.2): collection tags by the
						// vocabulary's name (else the row's tagTitles), item
						// tags by their item's title, my private tags muted. A
						// tag is changed from the item (⋯ Tag…) or a selection
						// (Tags…), never in the cell.
						base = {
							title: 'Tags',
							key: 'tags',
							className: 'card-table__tags',
							render: (_, item) => {
								const entries = itemTagEntries(item, { vocabulary: tagVocabulary, privateTags, titles: rememberedItemTagTitles() });
								// Each chip opens its tag page (T5); its click is its
								// own, never the row's select.
								return entries.length ? <ItemTagChips entries={entries} linked isolateClicks /> : '—';
							},
						};
					} else {
						base = {
							title: 'Created by',
							key: 'owner',
							render: (_, item) => (item?.owner ? <UserRef userId={normalizeId(item.owner)} size={20} /> : '—'),
						};
					}
					return {
						...base,
						...width,
						...fixed,
						key,
						render: (value, item, rowIndex) => wrap(item, rowIndex, base.render(value, item), readOnly(item)),
					};
				}

				const def = defsByName.get(column.element);
				const isLong = def?.dataType === 'text-large' || def?.dataType === 'rich-text';
				return {
					title: columnLabel(column, defsByName),
					key,
					...(isLong && !column.width ? { width: LONG_TEXT_WIDTH } : {}),
					...width,
					...fixed,
					className: 'card-table__value',
					render: (_, item, rowIndex) => {
						const display = renderElementValue(def, item, column);
						if (!inlineEdit) return display;
						const editability = cellEditability({ column, def, item, collection, cardsById });
						const id = normalizeId(item?._id);
						const cell = wrap(item, rowIndex, display, editability);
						if (!editing || editing.itemId !== id || editing.name !== def.name) return cell;
						return (
							<TableCellEditor
								open
								item={item}
								element={def}
								elementDefs={cardDefs}
								cardMeta={cardMeta}
								initialValue={Object.prototype.hasOwnProperty.call(editing, 'value') ? editing.value : item?.elements?.[def.name]}
								errorMessage={editing.error || null}
								isMobile={isMobile}
								recordTitle={def.dataType === 'rich-text' ? (
									<RecordTitle
										kind="item"
										record={cardsById?.[normalizeId(item?.card_id)] || null}
										size="sm"
										title={getItemDisplayValue({ card: cardsById?.[normalizeId(item?.card_id)] || null, elementDefs: resolveElements(item?.card_id), item, resolveLookupValue })}
									/>
								) : null}
								onSave={value => commitCell(item, def.name, value)}
								onCancel={cancelCell}
							>
								{cell}
							</TableCellEditor>
						);
					},
				};
			}),
			actionsColumn,
		];
	}, [cardsById, resolveElements, resolveLookupValue, uniqueCardId, uniquePrimaryDate, uniqueOrdered, resolvePrimaryDate, collection, collectionId, onItemDeleted, onItemUpdated, unitSystem, isMobile, titleWidth, tableColumns, defsByName, cardDefs, inlineEdit, editing, pending, openCell, commitCell, cancelCell, tableId, openItem, showCollectionColumn, tagVocabulary, privateTags, crossCollection, rankOrder]);

	const columns = useMemo(() => {
		if (!showCollectionColumn || !hasSubCollections(collection)) return baseColumns;
		const collectionColumn = {
			title: 'Sub-collection',
			key: 'sys:collection',
			width: 180,
			className: 'items-table__collection',
			render: (_, item) => {
				const where = familyMemberOf(item, collection) || collection;
				return <RecordBadge kind="collection" record={where} title={where?.name || 'Collection'} size="xs" />;
			},
		};
		return [baseColumns[0], collectionColumn, ...baseColumns.slice(1)];
	}, [baseColumns, showCollectionColumn, collection]);

	// A manual order (KPR-1396): the grip column first, on a desktop. The
	// rows the drag reads are the ones on screen, kept in a ref for the menu.
	const dragOn = Boolean(rankOrder?.active) && !isMobile;
	const rankColumns = useMemo(() => {
		if (!dragOn) return columns;
		const handleColumn = {
			title: '',
			onHeaderCell: () => ({ 'aria-label': 'Order' }),
			key: 'sys:rank-handle',
			width: RANK_HANDLE_WIDTH,
			fixed: 'left',
			className: 'items-table__rank-handle-cell',
			render: (_, item) => (
				<RankHandle
					title={getItemDisplayValue({ card: cardsById?.[normalizeId(item?.card_id)] || null, elementDefs: resolveElements(item?.card_id), item, resolveLookupValue }) || 'this item'}
					reason={rankOrder.blocked(item)}
					onStep={kind => rankOrder.step(item, kind, rowsRef.current)}
				/>
			),
		};
		return [handleColumn, ...columns];
	}, [dragOn, columns, rankOrder, cardsById, resolveElements, resolveLookupValue]);
	const cellColumns = useMemo(() => memoiseCellRenders(rankColumns, formats), [rankColumns, formats]);
	// The header layer (KPR-1638): sort, Hide and Freeze, over the memoised cells.
	const headerColumns = useTableHeaders({
		columns: cellColumns, headerMenu, cardId: uniqueCardId, cardDefs, defsByName, collectionId, crossCollection, isMobile,
		layoutColumns: resolvedColumns, layoutBody: uniqueCardId ? tableLayouts?.[uniqueCardId]?.body : null,
	});
	const rankSensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 6 } }));
	const rowIds = useMemo(() => (rows || []).map(item => normalizeId(item?._id)), [rows]);
	const onRankDragEnd = useCallback(({ active, over }) => {
		if (!over || active.id === over.id) return;
		const list = rowsRef.current || [];
		const item = list.find(row => normalizeId(row?._id) === `${active.id}`);
		const toIndex = list.findIndex(row => normalizeId(row?._id) === `${over.id}`);
		if (!item || toIndex < 0) return;
		rankOrder.moveTo(item, toIndex, list);
	}, [rankOrder]);

	// The selection column and the row props, identity-stable (KPR-1557).
	const { rowSelection, rowClassName, onRow } = useItemsTableRowProps({
		selectedIds, selectedItemId, selectionDisabledFor, onSelectionChange, onSelectionAnchor, onToggleSelect, onSelectItem,
	});

	const expandable = typeof renderExpanded === 'function' ? {
		expandedRowKeys: expandedItemId ? [expandedItemId] : [],
		expandedRowRender: item => renderExpanded(item),
		showExpandColumn: false,
		expandedRowClassName: () => 'items-table__expanded-row',
	} : undefined;

	const table = (
		<Table
			className={`items-table${uniqueCardId ? ' card-table' : ''}`}
			// The title cell's CSS reads the column width to ellipsize inside it.
			style={{ '--items-table-title-w': `${titleWidth}px` }}
			size="small"
			rowKey={itemRowKey}
			columns={headerColumns}
			components={dragOn ? { body: { row: RankSortableRow } } : undefined}
			dataSource={rows}
			pagination={false}
			scroll={{ x: 'max-content' }}
			sticky={{ offsetHeader: stickyOffset }}
			expandable={expandable}
			rowSelection={rowSelection}
			rowClassName={rowClassName}
			onRow={onRow}
		/>
	);

	// First load, nothing to show: the shared row skeletons, not antd's spinner.
	if (loading && (items || []).length === 0) return <ItemsListSkeleton variant="table" />;

	const ordered = dragOn ? (
		<DndContext sensors={rankSensors} collisionDetection={closestCenter} onDragEnd={onRankDragEnd}>
			<SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
				{table}
			</SortableContext>
		</DndContext>
	) : table;
	const rankAttestation = rankOrder?.attestation || null;
	const rankSign = rankAttestation ? (
		<AttestationModal
			open
			mode={rankAttestation.mode}
			collectionId={collectionId}
			cardId={rankAttestation.cardId}
			onSigned={(token) => { const { onSigned } = rankAttestation; rankOrder.setAttestation(null); onSigned(token); }}
			onClose={() => { const { onCancel } = rankAttestation; rankOrder.setAttestation(null); onCancel?.(); }}
		/>
	) : null;

	if (!inlineEdit) return rankSign ? <>{ordered}{rankSign}</> : ordered;
	const { attestation, conflict } = cellEdit;
	return (
		<CellMenuContext.Provider value={cellMenu.context}>
			<div data-card-table={tableId} className="card-table__frame">
				{ordered}
				{cellMenu.node}
				{rankSign}
				{conflict ? (
					<ItemConflictModal
						open
						title="Someone changed this field"
						context="cell"
						item={conflict.item}
						since={conflict.since}
						conflicts={conflict.conflicts}
						onSave={conflict.onSave}
						onCancel={conflict.onCancel}
					/>
				) : null}
				{attestation ? (
					<AttestationModal
						open
						mode={attestation.mode}
						collectionId={collectionId}
						cardId={attestation.cardId}
						onSigned={token => { const { onSigned } = attestation; cellEdit.setAttestation(null); onSigned(token); }}
						onClose={() => { const { onCancel } = attestation; cellEdit.setAttestation(null); onCancel?.(); }}
					/>
				) : null}
			</div>
		</CellMenuContext.Provider>
	);
}
